import { type RefObject, useRef, useState } from "react";

import { trpc } from "@blis/trpc/react";

import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader } from "@/components/ui/loader";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Sheet } from "@/components/ui/sheet";
import { Tab, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { ResultForm } from "@/features/runs/components/ResultForm";
import type { TAccessionDetail } from "@/lib/types";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { useDialogState } from "../detail/state";

export function ResultSheet({ accession }: { accession: TAccessionDetail }) {
  const returnFocus = useRef<HTMLButtonElement>(null);
  const [openTo, setOpenTo] = useDialogState("enterResults");
  return (
    <Sheet
      // hideClose
      // className={"w-full md:max-w-2xl h-full"}
      // onEscapeKeyDown={(evt) => {
      //   // See https://github.com/radix-ui/primitives/issues/1546
      //   returnFocus.current?.focus();
      //   evt.stopPropagation();
      // }}
      isOpen={!!openTo}
      onOpenChange={(flag) => !flag && setOpenTo(null)}
      side="right"
      classNames={{
        // full width on mobile
        content: "left-2 w-auto sm:left-auto sm:right-2 sm:max-w-2xl",
      }}
    >
      <Dialog>
        <DialogHeader>
          <DialogTitle>Enter results</DialogTitle>
        </DialogHeader>
        <DialogBody className="p-0! overflow-hidden">
          {openTo && (
            <FormWrapper
              accession={accession}
              openTo={openTo}
              doneRef={returnFocus}
            />
          )}
        </DialogBody>
      </Dialog>
    </Sheet>
  );
}

function FormWrapper({
  openTo,
  doneRef,
  accession,
}: {
  openTo: { orderId: number; resultId?: number } | null;
  doneRef: RefObject<HTMLButtonElement | null>;
  accession: TAccessionDetail;
}) {
  const tabRef = useRef<HTMLDivElement>(null);
  const [activeOrder, setActiveOrder] = useState(openTo);
  const [autofocus, setAutofocus] = useState(false);

  const { data, isLoading } = trpc.accession.resultEntryOrders.useQuery({
    accessionId: accession.id,
  });

  const allOrders = data || [];
  const orders = allOrders
    .filter((order) => !order.dependedById)
    .map((order) => {
      const reflexOrders = allOrders.filter((o) => o.dependedById === order.id);
      return {
        ...order,
        reflexOrders,
      };
    });
  const order = orders.find((order) => order.id === activeOrder?.orderId);

  const onLastResultComplete = () => {
    const currentIndex = orders.findIndex(
      (order) => order.id === activeOrder?.orderId,
    );
    const next = orders[currentIndex + 1];
    if (next) {
      setAutofocus(true);
      setActiveOrder({ orderId: next.id });
    } else {
      doneRef.current?.focus();
    }
  };

  useTinyKeys(
    {
      ArrowLeft: () => {
        tabRef.current?.focus();
      },
    },
    [],
    false,
  );

  return isLoading ? (
    <div className="flex-1 flex flex-col h-full items-center justify-center">
      <Loader size="medium" />
    </div>
  ) : order ? (
    <div className="flex-1 flex flex-col h-full md:flex-row">
      <Tabs
        selectedKey={order.id.toString()}
        onSelectionChange={(id) => {
          setAutofocus(false);
          setActiveOrder({ orderId: Number(id) });
        }}
        orientation="vertical"
        variant="pills"
        className="hidden md:flex md:w-[250px] h-full"
      >
        <TabsList className="w-full h-full justify-start overflow-y-auto p-2">
          {orders.map((order) => (
            <Tab
              key={order.id}
              id={order.id.toString()}
              className="inline-flex w-full min-w-0 flex-col items-stretch gap-1 px-2.5 py-1.5 text-left cursor-default"
            >
              <span className="min-w-0 truncate text-left">
                {order.testSpec.name}
              </span>
            </Tab>
          ))}
        </TabsList>
      </Tabs>
      <div className="px-6 pb-2 block md:hidden">
        <Select
          selectedKey={order.id}
          onSelectionChange={(val) => {
            setActiveOrder({ orderId: Number(val) });
          }}
          aria-label="Select test order"
        >
          <SelectTrigger />
          <SelectList items={orders}>
            {(item) => (
              <SelectItem
                id={item.id}
                key={item.id}
                textValue={item.testSpec.name}
              >
                <span className="min-w-0 truncate">{item.testSpec.name}</span>
              </SelectItem>
            )}
          </SelectList>
        </Select>
      </div>
      <ResultForm
        order={order}
        onLastResultComplete={onLastResultComplete}
        includeReflex
        startValue={
          order.results
            .find((r) => r.id === activeOrder?.resultId)
            ?.id.toString() || order.results[0]?.id.toString()
        }
        autoFocus={autofocus}
        className="flex-1 px-5 pt-1 h-full overflow-y-auto"
      />
    </div>
  ) : (
    <Text muted>Test order not found</Text>
  );
}
